import React from "react";
import { JoinButton } from "~/components/molecules/JoinButton";
import BreadcrumbsBar, { Breadcrumb } from "~/components/molecules/Breadcrumbs";
import { Form, useLocation } from "react-router";
import shopLogo from "~/assets/shop-img-academy-logo-sm-w.svg";
import caretTriangleDown from "~/assets/caret-triangle-down.svg";
import Image from "~/components/atoms/Image";
import Navigation from "~/components/organisms/navigation-bar/Navigation";
import { RouteIds, useRouteData } from "~/lib/routing";
import AthleteAvatar from "~/components/molecules/AthleteAvatar";
import ProductBanner from "~/components/molecules/ProductBanner";

interface LinkItem {
    to: string;
    label: React.ReactNode;
    dataTestID: string;
    className?: string;
    external?: boolean;
}

const AUDIENCE_LINKS: LinkItem[] = [
    {
        to: "https://www.imgacademy.com/about-img-academy",
        label: (
            <div className="flex gap-1">
                About <span className="hidden md:flex">Us</span>
            </div>
        ),
        dataTestID: "header-about-link",
    },
    {
        to: "https://shop.imgacademy.com",
        label: <Image src={shopLogo} alt="logo" />,
        className: "flex h-[22px] w-[70px]",
        dataTestID: "header-shop-link",
    },
    {
        to: "https://www.imglegacyhotel.com",
        label: (
            <div className="flex gap-1">
                <span className="hidden md:block">Legacy</span>Hotel
            </div>
        ),
        dataTestID: "header-hotel-link",
        external: true,
    },
    {
        to: "https://www.imgacademy.com/foundation",
        label: "Foundation",
        dataTestID: "header-foundation-link",
    },
    {
        to: "https://imgacademy.com/resources",
        label: "Resources",
        dataTestID: "header-resources-link",
    },
];

const AudienceBar = () => (
    <div className="flex h-[30px] bg-primary font-light md:justify-center">
        <div className="flex w-full max-w-[1290px] justify-around py-[5px] text-[15px] text-white md:justify-end md:gap-3 md:pr-6">
            {AUDIENCE_LINKS.map((link, index) => (
                <React.Fragment key={link.dataTestID}>
                    <a
                        href={link.to}
                        className={link.className || ""}
                        target={link.external ? "_blank" : "_self"}
                        rel={link.external ? "nofollow noopener noreferrer" : undefined}
                        data-testid={link.dataTestID}
                    >
                        {link.label}
                    </a>
                    {index < AUDIENCE_LINKS.length - 1 && <span aria-hidden="true">|</span>}
                </React.Fragment>
            ))}
        </div>
    </div>
);
AudienceBar.displayName = "AudienceBar";

interface LogoProps {
    isPlus?: boolean;
}
const Logo = ({ isPlus }: LogoProps) => {
    const classes = isPlus ? "logo-plus-icon sm:logo-plus-text" : "logo-icon sm:logo-text";
    return (
        <a href="/" aria-label="Return home">
            <div role="img" aria-description="IMGA Premier Home Logo" className={classes} />
        </a>
    );
};
Logo.displayName = "Logo";

const UserDropdown = () => (
    <div
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        className="group relative ml-auto hidden sm:flex"
    >
        <div className="flex items-center gap-[15px]">
            <AthleteAvatar size="header" />
            <Image src={caretTriangleDown} alt="Dropdown Arrow" className="size-[10px]" />
        </div>
        <div className="absolute bottom-[-40px] right-[-10px] z-10 hidden w-max flex-col border border-gray-80 bg-white px-4 py-2 shadow-2xl group-hover:flex group-hover:h-full">
            <Form action="/auth/logout" method="post" className="mt-auto">
                <button className="text-xl underline">Log out</button>
            </Form>
        </div>
    </div>
);
UserDropdown.displayName = "UserDropdown";

const AuthenticatedHeader = () => (
    <header className="bg-white">
        <div className="flex flex-col items-center">
            <div className="relative flex w-full max-w-[1290px] items-center justify-center px-4 sm:justify-start">
                <div className="flex min-h-[60px] items-center px-4 sm:px-0">
                    <Logo isPlus />
                </div>
                <Navigation isAuthenticated />
                <UserDropdown />
            </div>
            <hr className="w-full border-[0.5px] border-gray-20" />
        </div>
    </header>
);
AuthenticatedHeader.displayName = "AuthenticatedHeader";

interface UnauthenticatedHeaderProps {
    breadcrumbs?: Breadcrumb[];
    returnTo: string;
    showBetaBanner?: boolean;
}
const UnauthenticatedHeader = ({
    breadcrumbs,
    returnTo,
    showBetaBanner,
}: UnauthenticatedHeaderProps) => (
    <header className="bg-white">
        <AudienceBar />
        <div className="flex flex-col items-center">
            <div className="relative flex w-full max-w-[1290px] items-center justify-center sm:justify-between">
                <div className="flex min-h-[60px] items-center px-4">
                    <Logo />
                </div>
                <Navigation />
            </div>
            <hr className="w-full border-[0.5px] border-gray-20" />
            <div className="flex w-full max-w-[1290px] justify-between px-4 py-2">
                <BreadcrumbsBar items={breadcrumbs} />
                {!showBetaBanner && <JoinButton size="sm" data-testid="header-join" />}
            </div>
            {showBetaBanner && <ProductBanner loginReturnTo={returnTo} />}
        </div>
    </header>
);
UnauthenticatedHeader.displayName = "UnauthenticatedHeader";

interface HeaderProps {
    breadcrumbs?: Breadcrumb[];
}
export default function Header({ breadcrumbs }: HeaderProps) {
    const { FEATURES } = useRouteData(RouteIds.ROOT);
    const { authenticated } = useRouteData(RouteIds.AUTH_MIDDLEWARE);
    const location = useLocation();
    const returnTo = `${location.pathname}${location.search}`;

    if (authenticated) {
        return <AuthenticatedHeader />;
    }

    return (
        <UnauthenticatedHeader
            breadcrumbs={breadcrumbs}
            returnTo={returnTo}
            showBetaBanner={FEATURES.BETAExperience}
        />
    );
}

Header.displayName = "Header";
